import { Link, router } from "@inertiajs/react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  Search,
  ShoppingBag,
  User,
  Zap,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
} from "react";

import { Container } from "@/theme1/components/common/Container";
import MiniCart from "@/theme1/components/cart/MiniCart";
import CollectionsMegaMenu from "@/theme1/components/layout/CollectionsMegaMenu";
import SearchDialog from "@/theme1/components/search/SearchDialog";
import { useCart } from "@/theme1/hooks/useCart";
import { useWishlist } from "@/theme1/hooks/useWishlist";
import { usePage } from "@inertiajs/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/theme1/components/ui/dropdown-menu";

export function Header() {
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const { navigation = {}, auth = {}, settings = {} } = usePage().props;
  const user = auth.user ?? null;
  const mainNavigation = navigation.mainNavigation ?? [];
  const appLogo = settings.logo || null;
  const appName = settings.name || "Captex";

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);

  const { wishlistCount } = useWishlist();
  const { cartCount } = useCart();

  const updateScrollState = () => {
    const nav = navRef.current;

    if (!nav) return;

    setCanScrollLeft(nav.scrollLeft > 0);
    setCanScrollRight(
      nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1
    );
  };

  useEffect(() => {
    updateScrollState();

    const nav = navRef.current;

    if (!nav) return undefined;

    nav.addEventListener("scroll", updateScrollState, {
      passive: true,
    });

    window.addEventListener("resize", updateScrollState);

    return () => {
      nav.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "k"
      ) {
        event.preventDefault();
        setIsSearchOpen(true);
      }

      if (event.key === "Escape") {
        setIsSearchOpen(false);
        setIsMiniCartOpen(false);
        setIsCollectionsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target)
      ) {
        setIsCollectionsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutsideClick
      );
    };
  }, []);

  const scrollNav = (direction) => {
    const nav = navRef.current;

    if (!nav) return;

    const amount = Math.max(nav.clientWidth * 0.5, 240);

    nav.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const handleCollectionToggle = () => {
    setIsCollectionsOpen((current) => !current);
  };

  const closeCollections = () => {
    setIsCollectionsOpen(false);
  };

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 border-b border-black/10 bg-white text-black shadow-[0_1px_0_rgba(0,0,0,0.04)]"
    >
      <div className="bg-white">
        <Container>
          <div className="flex min-h-[78px] items-center justify-between gap-4 py-3">
            <Link
              href="/"
              onClick={closeCollections}
              className="group flex shrink-0 items-center gap-3 rounded-full pr-2 transition hover:bg-black/[0.03]"
            >
              {appLogo ? (
                <img
                  src={appLogo}
                  alt={appName}
                  className="size-12 rounded-2xl border border-black/10 bg-white object-contain p-1.5 shadow-[0_12px_30px_rgba(0,0,0,0.08)] transition group-hover:scale-[1.02]"
                />
              ) : (
                <div className="flex size-12 items-center justify-center rounded-2xl border border-black/10 bg-black font-black text-white shadow-[0_12px_30px_rgba(0,0,0,0.08)] transition group-hover:scale-[1.02]">
                  CT
                </div>
              )}

              <span className="hidden sm:block">
                <span className="block text-[10px] font-black uppercase tracking-[0.34em] text-black/40">
                  Captex
                </span>
                <span className="block text-base font-black uppercase tracking-[-0.03em]">
                  {appName}
                </span>
              </span>

              <span className="hidden items-center gap-2 rounded-full border border-black/10 bg-neutral-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-black/65 lg:inline-flex">
                <Zap className="size-3.5" />
                New arrival energy
              </span>
            </Link>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <button
                type="button"
                aria-label="Search"
                onClick={() => {
                  setIsSearchOpen(true);
                  closeCollections();
                }}
                className="inline-flex min-h-[42px] w-full max-w-[44px] items-center justify-center rounded-full border border-black/10 bg-white p-2.5 text-black shadow-sm transition hover:border-black/20 hover:bg-neutral-50 sm:max-w-[210px] sm:justify-between sm:px-4 sm:py-2 md:max-w-[240px]"
              >
                <span className="inline-flex items-center gap-2 text-sm text-black/55">
                  <Search className="size-4 shrink-0" />
                  <span className="hidden sm:inline">
                    Search
                  </span>
                </span>

                <kbd className="hidden rounded bg-neutral-900 px-2 py-0.5 text-[10px] font-medium text-white sm:ml-5 md:inline">
                  Ctrl K
                </kbd>
              </button>

              <Link
                href="/wishlist"
                aria-label="Wishlist"
                onClick={closeCollections}
                className="relative inline-flex items-center justify-center rounded-full p-2 transition hover:bg-black/5"
              >
                <Heart className="size-6" />

                {wishlistCount > 0 && (
                  <CounterBadge count={wishlistCount} />
                )}
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={user ? `${user.name} account menu` : "Account menu"}
                    onClick={closeCollections}
                    className="inline-flex items-center justify-center rounded-full p-2 transition hover:bg-black/5"
                  >
                    {user ? (
                      <span className="flex size-7 items-center justify-center rounded-full bg-black text-[11px] font-black uppercase text-white">
                        {getInitials(user.name)}
                      </span>
                    ) : (
                      <User className="size-6" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl bg-white p-2 text-black">
                  {user ? (
                    <>
                      <DropdownMenuLabel>
                        <span className="block truncate">{user.name}</span>
                        <span className="block truncate text-xs font-normal text-black/50">{user.email}</span>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <AccountLink href="/account">My Account</AccountLink>
                      <AccountLink href="/account/orders">Orders</AccountLink>
                      <AccountLink href="/wishlist">Wishlist</AccountLink>
                      <AccountLink href="/settings/profile">Profile</AccountLink>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => router.post("/logout")}
                        className="cursor-pointer text-red-600"
                      >
                        Logout
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <AccountLink href="/login">Login</AccountLink>
                      <AccountLink href="/register">Register</AccountLink>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                type="button"
                aria-label="Shopping Cart"
                onClick={() => {
                  setIsMiniCartOpen(true);
                  closeCollections();
                }}
                className="relative inline-flex items-center justify-center rounded-full p-2 transition hover:bg-black/5"
              >
                <ShoppingBag className="size-6" />

                {cartCount > 0 && (
                  <CounterBadge count={cartCount} />
                )}
              </button>
            </div>
          </div>
        </Container>
      </div>

      <div className="relative border-t border-black/10 bg-[#111111]">
        <Container>
          <div className="relative">
            {canScrollLeft && (
              <button
                type="button"
                aria-label="Scroll menu left"
                onClick={() => scrollNav("left")}
                className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/10 bg-[#242424] p-1 text-white shadow-xl shadow-black/30 transition hover:bg-[#303030]"
              >
                <ChevronLeft className="size-5" />
              </button>
            )}

            {canScrollRight && (
              <button
                type="button"
                aria-label="Scroll menu right"
                onClick={() => scrollNav("right")}
                className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/10 bg-[#242424] p-1 text-white shadow-xl shadow-black/30 transition hover:bg-[#303030]"
              >
                <ChevronRight className="size-5" />
              </button>
            )}

            <nav
              ref={navRef}
              aria-label="Main navigation"
              className="flex items-center justify-start gap-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              {mainNavigation.map((item, index) => {
                const baseClasses = [
                  "flex items-center gap-1 whitespace-nowrap py-4 text-[13px] font-semibold uppercase tracking-[0.12em] transition",
                  index === 0 ? "pr-4" : "px-4",
                  index === mainNavigation.length - 1
                    ? "pl-4"
                    : "",
                  item.highlight
                    ? "text-orange-300"
                    : "text-white/95",
                  item.accent ? "text-emerald-400" : "",
                ].join(" ");

                if (item.dropdown) {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={handleCollectionToggle}
                      aria-expanded={isCollectionsOpen}
                      aria-controls="collections-mega-menu"
                      className={`${baseClasses} rounded-t border-x border-white/15 bg-[#0b0b0b] hover:text-orange-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-300 ${
                        isCollectionsOpen
                          ? "text-orange-400"
                          : ""
                      }`}
                    >
                      {item.label}

                      <ChevronDown
                        className={`size-4 transition-transform duration-200 ${
                          isCollectionsOpen
                            ? "rotate-180"
                            : ""
                        }`}
                      />
                    </button>
                  );
                }

                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={closeCollections}
                    className={`${baseClasses} hover:text-orange-300`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </Container>

        <CollectionsMegaMenu
          open={isCollectionsOpen}
          onClose={closeCollections}
        />
      </div>

      <SearchDialog
        open={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      <MiniCart
        open={isMiniCartOpen}
        onClose={() => setIsMiniCartOpen(false)}
      />
    </header>
  );
}

function CounterBadge({ count }) {
  return (
    <span className="absolute right-0.5 top-0.5 flex size-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function AccountLink({ href, children }) {
  return (
    <DropdownMenuItem asChild>
      <Link href={href} className="w-full cursor-pointer">{children}</Link>
    </DropdownMenuItem>
  );
}

function getInitials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("") || "U";
}
